import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  BedDouble, Bike, Bus, Calendar, Car, CircleDashed, Coffee, Plane, Ship, Sparkles, Train, TramFront, Users, UtensilsCrossed, ShieldCheck,
  AlertCircle, Radio, Calculator, Clock, BellOff, Check, ChevronDown, ChevronUp,
} from 'lucide-react';
import type { EventCategory, EventWithPeople, Trip, TripStop } from '../domain/model';
import { normLocal, TRANSPORT_CATEGORIES } from '../domain/model';
import { applyAttentionActions, checkItinerary, type Finding } from '../domain/checks';
import { formatDate, formatTime, zoneAbbreviation } from '../domain/time';
import { useApp } from '../data/AppProvider';
import { Photo, slotForPlaces } from '../lib/imagery';
import { keys, useAction, useAttention } from '../data/hooks';
import { Badge, Button, Field, Input, Notice, SeverityBadge, Sheet, cx } from './ui';

export const CATEGORY_META: Record<EventCategory, { label: string; icon: typeof Plane }> = {
  flight: { label: 'Flight', icon: Plane }, rail: { label: 'Train', icon: Train }, coach: { label: 'Coach', icon: Bus }, ferry: { label: 'Ferry', icon: Ship },
  cruise: { label: 'Cruise', icon: Ship }, road: { label: 'Drive', icon: Car }, rental: { label: 'Rental', icon: Car }, transfer: { label: 'Transfer', icon: TramFront },
  accommodation: { label: 'Stay', icon: BedDouble }, activity: { label: 'Activity', icon: Bike }, meal: { label: 'Meal', icon: UtensilsCrossed },
  meeting: { label: 'Meeting', icon: Users }, free_time: { label: 'Free time', icon: Coffee }, other: { label: 'Other', icon: CircleDashed },
};

export function CategoryIcon({ category, className }: { category: EventCategory; className?: string }) {
  const Icon = CATEGORY_META[category]?.icon ?? CircleDashed;
  return <Icon className={cx('size-5', className)} aria-hidden />;
}

/** The four fact states from the brief, always with an icon and words. */
export function FactBadge({ state }: { state: 'confirmed' | 'live' | 'estimate' | 'needs_confirmation' }) {
  if (state === 'confirmed') return <Badge tone="forest" icon={<ShieldCheck className="size-3.5" aria-hidden />}>Confirmed booking</Badge>;
  if (state === 'live') return <Badge tone="info" icon={<Radio className="size-3.5" aria-hidden />}>Live update</Badge>;
  if (state === 'estimate') return <Badge tone="gold" icon={<Calculator className="size-3.5" aria-hidden />}>Estimate</Badge>;
  return <Badge tone="warn" icon={<AlertCircle className="size-3.5" aria-hidden />}>Needs confirmation</Badge>;
}

export function eventFactState(e: EventWithPeople): 'confirmed' | 'needs_confirmation' | null {
  const timeOk = e.start_precision === 'datetime' && (e.start_zone_status === 'resolved' || e.start_zone_status === 'user_resolved');
  if (e.status === 'suggested') return null;
  if (!timeOk && (e.source === 'booking' || TRANSPORT_CATEGORIES.has(e.category))) return 'needs_confirmation';
  if (e.source === 'booking' && e.status !== 'canceled') return 'confirmed';
  return null;
}

/** "09:01 BST → 12:05 CEST" — both zones shown when a journey crosses zones. */
export function EventTime({ e, showDate, viewerTz }: { e: EventWithPeople; showDate?: boolean; viewerTz?: string | null }) {
  if (e.start_precision === 'unknown') return <span className="text-warn">Time not set</span>;
  if (e.start_precision === 'date' || !e.start_utc) {
    const local = normLocal(e.start_local);
    if (e.start_precision === 'datetime' && local) {
      return <span className="text-warn">{local.slice(11)} <span className="text-xs">({e.start_zone_status === 'no_zone' ? 'time zone missing' : e.start_zone_status === 'ambiguous' ? 'repeated hour — confirm' : 'skipped hour — check'})</span></span>;
    }
    if (e.category === 'accommodation' && e.start_date) return <span>{formatDate(e.start_date)} → {e.end_date ? formatDate(e.end_date) : '?'}</span>;
    return <span>{e.start_date ? formatDate(e.start_date) : ''} <span className="text-warn">· time not set</span></span>;
  }
  const startTz = e.start_tz!;
  const endTz = e.end_tz ?? startTz;
  const crossZone = e.end_utc && endTz !== startTz;
  const sAbbr = zoneAbbreviation(e.start_utc, startTz);
  return (
    <span className="tabular-nums">
      {showDate && e.start_date && <>{formatDate(e.start_date)} · </>}
      {formatTime(e.start_utc, startTz)}{(crossZone || (viewerTz && viewerTz !== startTz)) && <span className="text-xs text-muted"> {sAbbr}</span>}
      {e.end_utc && (
        <>
          {' → '}
          {formatTime(e.end_utc, endTz)}
          {crossZone && <span className="text-xs text-muted"> {zoneAbbreviation(e.end_utc, endTz)}</span>}
          {e.end_date && e.start_date && e.end_date !== e.start_date && <span className="text-xs text-muted"> (+{Math.round((Date.parse(e.end_date) - Date.parse(e.start_date)) / 86_400_000)}d)</span>}
        </>
      )}
    </span>
  );
}

export function AudienceBadge({ e, subgroupName, memberCount }: { e: EventWithPeople; subgroupName?: string; memberCount: number }) {
  if (e.visibility === 'private') return <Badge>Only you</Badge>;
  if (e.visibility === 'subgroup') return <Badge tone="gold" icon={<Users className="size-3" aria-hidden />}>{subgroupName ?? 'Subgroup'}</Badge>;
  if (e.participant_ids.length > 0 && e.participant_ids.length < memberCount) return <Badge tone="gold" icon={<Users className="size-3" aria-hidden />}>{e.participant_ids.length} going</Badge>;
  return null;
}

export function useFindings(trip: Trip | undefined, stops: TripStop[], events: EventWithPeople[], memberIds: string[], mode: 'my' | 'group') {
  const { session } = useApp();
  const actions = useAttention(trip?.id);
  return useMemo(() => {
    if (!trip) return { visible: [] as Finding[], hidden: [] as { finding: Finding }[], loading: true };
    const all = checkItinerary(trip, stops, events, { now: new Date(), memberIds: memberIds.length ? memberIds : [session!.userId] });
    const relevant = mode === 'group' ? all : all.filter((f) => f.personIds.length === 0 || f.personIds.includes(session!.userId));
    const r = applyAttentionActions(relevant, actions.data ?? [], new Date());
    return { ...r, loading: actions.isLoading };
  }, [trip, stops, events, memberIds, mode, actions.data, actions.isLoading, session]);
}

export function AttentionList({ tripId, findings, compact, onResolve }: { tripId: string; findings: Finding[]; compact?: boolean; onResolve?: (f: Finding) => void }) {
  const { data } = useApp();
  const [dismissing, setDismissing] = useState<Finding | null>(null);
  const [reason, setReason] = useState('');
  const [expanded, setExpanded] = useState<string | null>(null);
  const record = useAction((f: Finding, action: 'dismissed' | 'snoozed' | 'resolved', why: string | null, until: string | null) => data.recordAttention(tripId, f.fingerprint, action, why, until),
    { invalidate: [keys.attention(tripId)], success: 'Updated' });
  const limit = compact ? 3 : 50;

  if (findings.length === 0) {
    return <Notice tone="success" title="All clear">Times, connections and stays look consistent. For live disruption, check the operator.</Notice>;
  }
  return (
    <>
      <ul className="space-y-2">
        {findings.slice(0, limit).map((f) => {
          const open = expanded === f.fingerprint;
          return (
            <li key={f.fingerprint} className={cx('rounded-xl border bg-white p-3', f.severity === 'high' ? 'border-danger/30' : 'border-line')}>
              <div className="flex flex-wrap items-start gap-2">
                <SeverityBadge severity={f.severity} />
                <div className="min-w-0 flex-[1_1_14rem]">
                  <p className="font-medium leading-snug break-anywhere">{f.title}</p>
                  <p className="mt-0.5 text-sm text-muted">{f.impact}</p>
                </div>
              </div>
              {open && (
                <div className="mt-2 space-y-1 rounded-lg bg-ivory p-2.5 text-sm">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted">Evidence</p>
                  <ul className="list-inside list-disc text-ink/90">{f.evidence.map((x, i) => <li key={i} className="break-anywhere">{x}</li>)}</ul>
                  {f.calculation && <p><span className="font-medium">Calculation:</span> {f.calculation}</p>}
                  <p className="text-xs text-muted">Checked {new Date(f.checkedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}{f.incomplete ? ' · check incomplete — some inputs are unknown' : ''}</p>
                </div>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {onResolve && <Button size="sm" onClick={() => onResolve(f)}>{f.action.label}</Button>}
                <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setExpanded(open ? null : f.fingerprint)} icon={open ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}>{open ? 'Hide evidence' : 'Evidence'}</Button>
                <Button size="sm" variant="ghost" icon={<Clock className="size-4" />} onClick={() => record.mutate([f, 'snoozed', null, new Date(Date.now() + 3 * 3600_000).toISOString()])}>Snooze 3h</Button>
                <Button size="sm" variant="ghost" icon={<BellOff className="size-4" />} onClick={() => { setDismissing(f); setReason(''); }}>Dismiss</Button>
              </div>
            </li>
          );
        })}
      </ul>
      {findings.length > limit && <p className="mt-2 text-sm text-muted">+{findings.length - limit} more in the itinerary</p>}
      <Sheet open={Boolean(dismissing)} onClose={() => setDismissing(null)} title="Dismiss this item?"
        footer={<>
          <Button variant="secondary" onClick={() => setDismissing(null)}>Cancel</Button>
          <Button variant="secondary" icon={<Check className="size-4" />} onClick={() => { record.mutate([dismissing!, 'resolved', reason || null, null]); setDismissing(null); }}>I've resolved it</Button>
          <Button onClick={() => { record.mutate([dismissing!, 'dismissed', reason || null, null]); setDismissing(null); }}>Dismiss</Button>
        </>}>
        <p className="mb-3 text-sm text-muted">{dismissing?.title}. It will come back if the related details change.</p>
        <Field label="Reason (optional)" hint="Saved with the item's history, visible only to you.">
          {(id, d) => <Input id={id} aria-describedby={d} value={reason} maxLength={300} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Checked with the hotel" />}
        </Field>
      </Sheet>
    </>
  );
}

export function TripDates({ trip }: { trip: Trip }) {
  if (trip.date_mode === 'unknown') return <span>Dates not decided</span>;
  if (!trip.start_date) return <span>{trip.date_note ?? 'Flexible dates'}</span>;
  return <span><span className="whitespace-nowrap"><Calendar className="mr-1 inline size-3.5 align-[-2px]" aria-hidden />{formatDate(trip.start_date)} –</span> <span className="whitespace-nowrap">{trip.end_date ? formatDate(trip.end_date) : '?'}</span>{trip.date_mode === 'flexible' && ' (flexible)'}</span>;
}

export function TripCard({ trip, badge }: { trip: Trip; badge?: React.ReactNode }) {
  return (
    <article className="lift group h-full overflow-hidden rounded-3xl bg-white shadow-[var(--shadow-card)]">
      <Link to={`/trips/${trip.id}`} className="flex h-full flex-col">
        <Photo slot={slotForPlaces(trip.title, trip.origin_city)} width={720} overlay="bottom" className="min-h-48 flex-1">
          <div className="flex flex-1 flex-col justify-between gap-6 p-3 pb-4">
            <span className="flex flex-wrap gap-1.5">
              {badge}
              {trip.group_id && <Badge tone="gold" className="bg-white/95" icon={<Users className="size-3" aria-hidden />}>Group trip</Badge>}
            </span>
            <div className="px-1 text-white">
              <h3 className="line-clamp-2 font-editorial text-2xl leading-tight text-white drop-shadow break-words" title={trip.title}>{trip.title}</h3>
              <p className="mt-0.5 text-sm text-white/90"><TripDates trip={trip} /></p>
            </div>
          </div>
        </Photo>
      </Link>
    </article>
  );
}

export function SuggestionTag() {
  return <Badge tone="info" icon={<Sparkles className="size-3" aria-hidden />}>Suggestion — not booked</Badge>;
}
